/**
 * Marriage meld logic: pure sequences, tunnelas, trials, joker (wild) substitution, maal points and
 * a solver used both by bots and by the server to double-check declarations.
 * All melds in Nepali Marriage are exactly three cards.
 */
import { parseCard, type Card, type CardId, type Rank, type Suit } from '../../core/cards.js';

export interface JokerContext {
  tiplu: CardId;
  /** Treat every card of the tiplu's rank as wild (standard Nepali rule). */
  sameRankWild: boolean;
  /** Poplu/Jhiplu (tiplu suit ±1) are wild. */
  neighboursWild: boolean;
  printedJokersWild: boolean;
}

export interface MaalValues {
  tiplu: number;
  poplu: number;
  jhiplu: number;
  alter: number;
  man: number;
  marriage: number;
  tunnela: number;
}

const up = (r: Rank): number => (r === 14 ? 2 : r + 1);
const down = (r: Rank): number => (r === 2 ? 14 : r - 1);
const ALT: Record<Suit, Suit> = { S: 'C', C: 'S', H: 'D', D: 'H' };

export function isWild(id: CardId, ctx: JokerContext | null): boolean {
  if (!ctx) return false;
  const c = parseCard(id);
  if (c.joker) return ctx.printedJokersWild;
  const t = parseCard(ctx.tiplu);
  if (ctx.sameRankWild && c.rank === t.rank) return true;
  if (c.rank === t.rank && c.suit === t.suit) return true;
  if (ctx.neighboursWild && c.suit === t.suit && (c.rank === up(t.rank) || c.rank === down(t.rank))) return true;
  return false;
}

export type MaalKind = 'TIPLU' | 'POPLU' | 'JHIPLU' | 'ALTER' | 'MAN';

export function maalKind(id: CardId, tiplu: CardId): MaalKind | null {
  const c = parseCard(id);
  if (c.joker) return 'MAN';
  const t = parseCard(tiplu);
  if (c.suit === t.suit && c.rank === t.rank) return 'TIPLU';
  if (c.suit === t.suit && c.rank === up(t.rank)) return 'POPLU';
  if (c.suit === t.suit && c.rank === down(t.rank)) return 'JHIPLU';
  if (c.rank === t.rank && c.suit === ALT[t.suit]) return 'ALTER';
  return null;
}

function consecutive(ranks: number[]): boolean {
  const tryOrder = (rs: number[]) => {
    const s = [...rs].sort((a, b) => a - b);
    return s.every((r, i) => i === 0 || r === s[i - 1]! + 1);
  };
  if (tryOrder(ranks)) return true;
  // Ace low: A-2-3.
  if (ranks.includes(14)) return tryOrder(ranks.map((r) => (r === 14 ? 1 : r)));
  return false;
}

export function isPureSequence(cards: readonly CardId[]): boolean {
  if (cards.length !== 3) return false;
  const cs = cards.map(parseCard);
  if (cs.some((c) => c.joker)) return false;
  if (!cs.every((c) => c.suit === cs[0]!.suit)) return false;
  return consecutive(cs.map((c) => c.rank));
}

export function isTunnela(cards: readonly CardId[]): boolean {
  if (cards.length !== 3) return false;
  const cs = cards.map(parseCard);
  if (cs.some((c) => c.joker)) return false;
  return cs.every((c) => c.rank === cs[0]!.rank && c.suit === cs[0]!.suit);
}

export function isTrial(cards: readonly CardId[]): boolean {
  if (cards.length !== 3) return false;
  const cs = cards.map(parseCard);
  if (cs.some((c) => c.joker)) return false;
  return cs.every((c) => c.rank === cs[0]!.rank) && new Set(cs.map((c) => c.suit)).size === 3;
}

/** Valid for the initial show (before seeing the joker). */
export function isPureMeld(cards: readonly CardId[]): boolean {
  return isPureSequence(cards) || isTunnela(cards);
}

function naturalsFit(naturals: Card[], wilds: number): boolean {
  if (naturals.length === 0) return wilds === 3;
  // Sequence with gaps filled by wilds.
  if (naturals.every((c) => c.suit === naturals[0]!.suit) && new Set(naturals.map((c) => c.rank)).size === naturals.length) {
    const fits = (rs: number[]) => Math.max(...rs) - Math.min(...rs) <= 2;
    const ranks = naturals.map((c) => c.rank as number);
    if (fits(ranks) || (ranks.includes(14) && fits(ranks.map((r) => (r === 14 ? 1 : r))))) return true;
  }
  // Trial: same rank, distinct suits.
  if (naturals.every((c) => c.rank === naturals[0]!.rank) && new Set(naturals.map((c) => c.suit)).size === naturals.length) return true;
  return false;
}

/** Any valid meld once the joker is known (wild substitution allowed). */
export function isMeld(cards: readonly CardId[], ctx: JokerContext | null): boolean {
  if (cards.length !== 3) return false;
  if (isPureSequence(cards) || isTunnela(cards) || isTrial(cards)) return true;
  if (!ctx) return false;
  const cs = cards.map(parseCard);
  const wildIdx = cards.map((id, i) => (isWild(id, ctx) ? i : -1)).filter((i) => i >= 0);
  if (wildIdx.length === 0) return false;
  // Each wild card may act as wild or as its natural self.
  for (let mask = 1; mask < 1 << wildIdx.length; mask++) {
    const asWild = new Set(wildIdx.filter((_, k) => mask & (1 << k)));
    const naturals = cs.filter((_, i) => !asWild.has(i));
    if (naturals.some((c) => c.joker)) continue;
    if (naturalsFit(naturals, asWild.size)) return true;
  }
  return false;
}

/** Every 3-card meld (by hand index) available in a hand. */
export function candidateMelds(hand: readonly CardId[], ctx: JokerContext | null, pureOnly: boolean): number[][] {
  const out: number[][] = [];
  const n = hand.length;
  const wild = hand.map((c) => isWild(c, ctx));
  const cards = hand.map(parseCard);
  for (let a = 0; a < n; a++)
    for (let b = a + 1; b < n; b++) {
      // Cheap pre-filter: two naturals must share a suit (sequence) or rank (trial/tunnela).
      if (!wild[a] && !wild[b] || pureOnly) {
        const ca = cards[a]!;
        const cb = cards[b]!;
        const related = ca.suit === cb.suit ? Math.abs(ca.rank - cb.rank) <= 2 || (ca.rank === 14 || cb.rank === 14) : ca.rank === cb.rank;
        if (!related) continue;
      }
      for (let c = b + 1; c < n; c++) {
        const trio = [hand[a]!, hand[b]!, hand[c]!];
        if (pureOnly ? isPureMeld(trio) : isMeld(trio, ctx)) out.push([a, b, c]);
      }
    }
  return out;
}

/** Finds `count` disjoint melds (indices) or null. */
export function findDisjointMelds(hand: readonly CardId[], count: number, ctx: JokerContext | null, pureOnly: boolean): number[][] | null {
  const cands = candidateMelds(hand, ctx, pureOnly);
  const chosen: number[][] = [];
  const used = new Set<number>();
  let steps = 0;
  const dfs = (start: number): boolean => {
    if (chosen.length === count) return true;
    if (++steps > 20000) return false;
    for (let i = start; i < cands.length; i++) {
      const m = cands[i]!;
      if (m.some((x) => used.has(x))) continue;
      m.forEach((x) => used.add(x));
      chosen.push(m);
      if (dfs(i + 1)) return true;
      chosen.pop();
      m.forEach((x) => used.delete(x));
    }
    return false;
  };
  return dfs(0) ? chosen.map((m) => [...m]) : null;
}

/**
 * Exact cover: partitions ALL cards into melds of 3 (bitmask DP, hands up to ~15 cards).
 * Returns groups of card ids or null.
 */
export function partitionIntoMelds(cards: readonly CardId[], ctx: JokerContext | null): CardId[][] | null {
  const n = cards.length;
  if (n % 3 !== 0) return null;
  if (n === 0) return [];
  if (n > 18) throw new Error('partitionIntoMelds supports at most 18 cards');
  const cands = candidateMelds(cards, ctx, false);
  const byFirst = new Map<number, number[]>();
  for (const m of cands) {
    const masks = byFirst.get(m[0]!) ?? [];
    masks.push((1 << m[0]!) | (1 << m[1]!) | (1 << m[2]!));
    byFirst.set(m[0]!, masks);
  }
  const full = (1 << n) - 1;
  const memo = new Map<number, number[] | null>();
  const solve = (used: number): number[] | null => {
    if (used === full) return [];
    const hit = memo.get(used);
    if (hit !== undefined) return hit;
    let first = 0;
    while (used & (1 << first)) first++;
    let res: number[] | null = null;
    for (const mask of byFirst.get(first) ?? []) {
      if (mask & used) continue;
      const rest = solve(used | mask);
      if (rest) {
        res = [mask, ...rest];
        break;
      }
    }
    memo.set(used, res);
    return res;
  };
  const sol = solve(0);
  if (!sol) return null;
  return sol.map((mask) => cards.filter((_, i) => mask & (1 << i)));
}

/** Maal points for a player who has seen the joker. `cards` = every card they hold (shown + hand). */
export function maalPoints(cards: readonly CardId[], tiplu: CardId, values: MaalValues, shownMelds: readonly CardId[][] = []): number {
  let pts = 0;
  const kinds = cards.map((c) => maalKind(c, tiplu));
  let poplu = 0;
  let jhiplu = 0;
  let tipluN = 0;
  for (const k of kinds) {
    if (k === 'TIPLU') tipluN++;
    if (k === 'POPLU') poplu++;
    if (k === 'JHIPLU') jhiplu++;
  }
  // Each Jhiplu+Tiplu+Poplu set counts as a "marriage" (bonus replaces individual values).
  const marriages = Math.min(poplu, jhiplu, tipluN);
  pts += marriages * values.marriage;
  pts += (tipluN - marriages) * values.tiplu + (poplu - marriages) * values.poplu + (jhiplu - marriages) * values.jhiplu;
  for (const k of kinds) {
    if (k === 'ALTER') pts += values.alter;
    if (k === 'MAN') pts += values.man;
  }
  for (const m of shownMelds) if (isTunnela(m)) pts += values.tunnela;
  return pts;
}

/**
 * Bot heuristic: how useful each card is (higher = keep). Considers wilds, pairs/near-sequences, maal.
 */
export function cardUtility(hand: readonly CardId[], ctx: JokerContext | null): number[] {
  const cards = hand.map(parseCard);
  return cards.map((c, i) => {
    if (isWild(hand[i]!, ctx)) return 100;
    if (c.joker) return 90;
    let score = 0;
    cards.forEach((o, j) => {
      if (i === j || o.joker) return;
      if (o.suit === c.suit) {
        const d = Math.abs(o.rank - c.rank);
        const dAce = c.rank === 14 || o.rank === 14 ? Math.abs((o.rank === 14 ? 1 : o.rank) - (c.rank === 14 ? 1 : c.rank)) : 99;
        const gap = Math.min(d, dAce);
        if (gap === 0) score += 6; // identical → tunnela potential
        else if (gap === 1) score += 5;
        else if (gap === 2) score += 3;
      } else if (o.rank === c.rank) score += 2.5; // trial potential (needs joker to complete unless 3 suits)
    });
    if (ctx && maalKind(hand[i]!, ctx.tiplu)) score += 8;
    // Middle ranks combine more ways than aces/kings.
    score += c.rank >= 4 && c.rank <= 11 ? 0.5 : 0;
    return score;
  });
}
